import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, ArrowLeft, Mail, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AVATAR = "/emily.jpg";

const getSession = () => {
  let s = localStorage.getItem("flowlux_chat_session");
  if (!s) {
    s = (crypto.randomUUID && crypto.randomUUID()) || `s-${Date.now()}-${Math.random()}`;
    localStorage.setItem("flowlux_chat_session", s);
  }
  return s;
};

const GREETING =
  "Hi, I'm Emily from FlowLux! 💜 I'd love to help you glow — ask me anything about the Red Light Mask, pricing, shipping or your order.";

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", text: GREETING }]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [view, setView] = useState("chat"); // chat | human
  const [human, setHuman] = useState({ name: "", email: "", message: "" });
  const [humanSent, setHumanSent] = useState(false);
  const sessionId = useRef(getSession());
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending, open, view]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setSending(true);
    try {
      const res = await axios.post(`${API}/chat/emily`, {
        session_id: sessionId.current,
        message: text,
      });
      setMessages((m) => [...m, { role: "assistant", text: res.data.reply, offerHuman: res.data.offer_human }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          text: "Sorry, I'm having a little trouble right now. You can reach our team directly at team@flowlux.co and we'll get right back to you!",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const submitHuman = async (e) => {
    e.preventDefault();
    if (!human.name || !human.email || !human.message) {
      toast.error("Please add your name, email and a short message");
      return;
    }
    try {
      await axios.post(`${API}/contact`, {
        name: human.name,
        email: human.email,
        subject: "Chat — Talk to a human",
        message: human.message,
      });
      setHumanSent(true);
    } catch {
      toast.error("Something went wrong. Please email team@flowlux.co");
    }
  };

  return (
    <>
      {/* Bubble */}
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="chat-bubble"
        aria-label="Chat with Emily"
        className="fixed bottom-5 right-5 z-[60] group"
      >
        <span className="absolute inset-0 rounded-full bg-[#6D28D9]/40 animate-ping" style={{ animationDuration: "2.5s" }} />
        <span className="relative flex items-center gap-2.5 pl-1.5 pr-4 py-1.5 rounded-full bg-[#1A1A1A] text-[#F4EFE8] shadow-xl transition-transform group-hover:scale-[1.03]">
          <span className="relative">
            <img src={AVATAR} alt="Emily" className="h-11 w-11 rounded-full object-cover border-2 border-[#F4EFE8]/20" />
            <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full bg-[#22c55e] border-2 border-[#1A1A1A]" />
          </span>
          {!open && (
            <span className="text-left leading-tight">
              <span className="block text-[13px] font-medium">Chat with Emily</span>
              <span className="block text-[10px] tracking-[0.08em] text-[#F4EFE8]/60 uppercase">FlowLux Sales Rep</span>
            </span>
          )}
        </span>
      </button>

      {/* Panel */}
      <div
        data-testid="chat-panel"
        className={`fixed bottom-24 right-5 z-[60] w-[calc(100vw-2.5rem)] max-w-[380px] origin-bottom-right transition-all duration-300 ${
          open ? "opacity-100 scale-100 pointer-events-auto" : "opacity-0 scale-95 pointer-events-none"
        }`}
      >
        <div className="flex flex-col h-[540px] max-h-[75vh] bg-[#F4EFE8] rounded-2xl shadow-2xl overflow-hidden border border-[#1A1A1A]/10">
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3.5 bg-[#1A1A1A] text-[#F4EFE8]">
            {view === "human" && (
              <button onClick={() => { setView("chat"); setHumanSent(false); }} className="hover:opacity-70" data-testid="chat-back-btn">
                <ArrowLeft size={18} />
              </button>
            )}
            <span className="relative">
              <img src={AVATAR} alt="Emily" className="h-10 w-10 rounded-full object-cover" />
              <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-[#22c55e] border-2 border-[#1A1A1A]" />
            </span>
            <div className="flex-1 leading-tight">
              <p className="text-[14px] font-medium">Emily</p>
              <p className="text-[11px] text-[#F4EFE8]/60">FlowLux Sales Rep · Online</p>
            </div>
            <button onClick={() => setOpen(false)} className="hover:opacity-70" aria-label="Close chat">
              <X size={19} />
            </button>
          </div>

          {view === "chat" ? (
            <>
              {/* Messages */}
              <div ref={scrollRef} className="flex-1 overflow-y-auto no-scrollbar px-4 py-4 space-y-3">
                {messages.map((m, i) => (
                  <div key={i} className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}>
                    <div
                      className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl text-[13.5px] leading-relaxed ${
                        m.role === "user"
                          ? "bg-[#1A1A1A] text-[#F4EFE8] rounded-br-sm"
                          : "bg-white text-[#1A1A1A] rounded-bl-sm shadow-sm"
                      }`}
                    >
                      {m.text}
                    </div>
                    {m.offerHuman && (
                      <button
                        onClick={() => setView("human")}
                        data-testid="chat-human-btn"
                        className="mt-2 text-[12px] px-3 py-1.5 rounded-full border border-[#6D28D9]/40 text-[#6D28D9] hover:bg-[#6D28D9]/5 transition-colors"
                      >
                        Talk to a human →
                      </button>
                    )}
                  </div>
                ))}
                {sending && (
                  <div className="flex justify-start">
                    <div className="bg-white rounded-2xl rounded-bl-sm shadow-sm px-4 py-3 flex gap-1">
                      {[0, 1, 2].map((d) => (
                        <span key={d} className="h-1.5 w-1.5 rounded-full bg-[#1A1A1A]/40 animate-bounce" style={{ animationDelay: `${d * 0.15}s` }} />
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Input */}
              <div className="flex items-end gap-2 px-3 py-3 border-t border-[#1A1A1A]/10 bg-[#F4EFE8]">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKey}
                  rows={1}
                  placeholder="Type your message…"
                  data-testid="chat-input"
                  className="flex-1 resize-none max-h-24 bg-white border border-[#1A1A1A]/15 rounded-xl px-3.5 py-2.5 text-[13.5px] outline-none focus:border-[#1A1A1A]/40"
                />
                <button
                  onClick={send}
                  disabled={sending || !input.trim()}
                  data-testid="chat-send"
                  className="h-10 w-10 rounded-xl bg-[#1A1A1A] text-[#F4EFE8] flex items-center justify-center disabled:opacity-40 transition-opacity"
                >
                  <Send size={16} />
                </button>
              </div>
            </>
          ) : (
            /* Human contact form */
            <div className="flex-1 overflow-y-auto no-scrollbar px-5 py-5">
              {humanSent ? (
                <div className="text-center pt-8">
                  <CheckCircle2 size={46} className="mx-auto mb-4 text-[#22c55e]" strokeWidth={1.4} />
                  <p className="font-serif-display text-[22px] mb-2">We've got your message!</p>
                  <p className="text-[13.5px] text-[#1A1A1A]/70 leading-relaxed mb-5">
                    A real member of the FlowLux team will get back to you shortly. You can also email us anytime at{" "}
                    <a href="mailto:team@flowlux.co" className="text-[#6D28D9] underline">team@flowlux.co</a>.
                  </p>
                  <button onClick={() => { setView("chat"); setHumanSent(false); }} className="btn-lux px-6 py-3 text-[11px] tracking-[0.2em] uppercase">
                    Back to chat
                  </button>
                </div>
              ) : (
                <form onSubmit={submitHuman} className="space-y-3.5">
                  <div>
                    <p className="font-serif-display text-[20px] leading-snug mb-1">Talk to a human</p>
                    <p className="text-[12.5px] text-[#1A1A1A]/60 leading-relaxed">
                      Leave your details and our team will reach out. Prefer email? Write us at{" "}
                      <a href="mailto:team@flowlux.co" className="text-[#6D28D9] underline">team@flowlux.co</a>.
                    </p>
                  </div>
                  <input
                    value={human.name}
                    onChange={(e) => setHuman((h) => ({ ...h, name: e.target.value }))}
                    placeholder="Your name"
                    data-testid="human-name"
                    className="w-full bg-white border border-[#1A1A1A]/15 rounded-lg px-3.5 py-2.5 text-[13.5px] outline-none focus:border-[#1A1A1A]/40"
                  />
                  <input
                    type="email"
                    value={human.email}
                    onChange={(e) => setHuman((h) => ({ ...h, email: e.target.value }))}
                    placeholder="Email address"
                    data-testid="human-email"
                    className="w-full bg-white border border-[#1A1A1A]/15 rounded-lg px-3.5 py-2.5 text-[13.5px] outline-none focus:border-[#1A1A1A]/40"
                  />
                  <textarea
                    value={human.message}
                    onChange={(e) => setHuman((h) => ({ ...h, message: e.target.value }))}
                    placeholder="How can we help? (order number, question, etc.)"
                    rows={4}
                    data-testid="human-message"
                    className="w-full resize-none bg-white border border-[#1A1A1A]/15 rounded-lg px-3.5 py-2.5 text-[13.5px] outline-none focus:border-[#1A1A1A]/40"
                  />
                  <button type="submit" data-testid="human-submit" className="btn-lux w-full py-3.5 text-[11px] tracking-[0.2em] uppercase gap-2">
                    <Mail size={14} /> Send to the team
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ChatWidget;
